import type { CSSProperties } from 'react'

export type DashboardReskinTileState = 'launch' | 'locked' | 'replay'

export const DASHBOARD_RESKIN_TILE_SIZE_CLASS = 'aspect-[326/448] w-full max-w-[calc(326px*0.8)]'
export const DASHBOARD_RESKIN_TILE_FLEX_CLASS = 'min-w-0 flex-1 overflow-visible w-full max-w-[calc(326px*0.8)]'
export const DASHBOARD_RESKIN_TILE_IMAGE_CLASS = 'h-full w-full object-contain'
export const DASHBOARD_RESKIN_LAUNCH_TILE_IMAGE_CLASS = 'absolute left-1/2 top-1/2 w-[calc(100%*371/326)] max-w-none -translate-x-1/2 -translate-y-1/2'
export const DASHBOARD_RESKIN_WORDMARK_SIZE_CLASS = 'h-[133px] w-[375px] -ml-[18px]'
export const DASHBOARD_RESKIN_MENU_TOP_PAD_CLASS = 'pt-[4px]'
export const DASHBOARD_RESKIN_MENU_INSET_CLASS = `px-[95px] ${DASHBOARD_RESKIN_MENU_TOP_PAD_CLASS}`
export const DASHBOARD_RESKIN_MENU_STAGE_CLASS = 'flex justify-center'
export const DASHBOARD_RESKIN_CHROME_MAX_CLASS = 'w-full max-w-[1720px]'
export const DASHBOARD_RESKIN_HEADER_BREAKOUT_CLASS = 'relative left-[calc(50%-50vw)] w-[100vw] max-w-[100vw] overflow-visible'
export const DASHBOARD_RESKIN_MENU_ICON_SIZE_CLASS = 'h-[98px] w-[204px] translate-x-[2px]'
export const DASHBOARD_RESKIN_MENU_LINK_CLASS = 'option bg-transparent p-0 underline cursor-pointer text-[24px] leading-none text-[#B4BBB8]'
export const DASHBOARD_RESKIN_PLUS_XP_CLASS = 'absolute right-[88px] top-1/2 z-30 -translate-y-1/2 font-bold tracking-tight text-[22px] text-[#101111] duration-[1000ms] ease-linear'
export const DASHBOARD_RESKIN_LIGHTNING_WIDTH_CLASS = 'w-[calc(100%-min(calc(326px*0.8),(100%-6%)/4))]'
export const DASHBOARD_RESKIN_LIGHTNING_SIZE_CLASS = 'block h-auto w-full max-w-none aspect-[1034/288]'
export const DASHBOARD_RESKIN_HEALTHBAR_ROW_HEIGHT_PX = 44
export const DASHBOARD_RESKIN_HEALTHBAR_TOP_PAD_PX = 10
export const DASHBOARD_RESKIN_HEALTHBAR_BOTTOM_RESERVE_PX = 72
export const DASHBOARD_RESKIN_HEALTHBAR_TOTAL_HEIGHT_PX = 54
export const DASHBOARD_RESKIN_HEALTHBAR_WRAP_CLASS = 'ml-[calc(50%-50vw)] w-[calc(50%+50vw)] shrink-0 overflow-visible pt-[10px]'
export const DASHBOARD_RESKIN_HEALTHBAR_BOTTOM_RESERVE_CLASS = 'h-[72px] w-full shrink-0'
export const DASHBOARD_RESKIN_HEALTHBAR_ROW_CLASS = 'relative z-10 h-[44px] w-full overflow-visible'
export const DASHBOARD_RESKIN_HEALTHBAR_VIDEO_GAP_CLASS = 'pt-[54px]'
export const DASHBOARD_RESKIN_COURSE_VIDEO_SIZE_CLASS = 'h-[72vh]'
export const DASHBOARD_RESKIN_TF_MODAL_CLASS = 'w-[132vh] h-[72vh]'
export const DASHBOARD_RESKIN_HEALTHBAR_TRACK_CLASS = 'absolute inset-y-0 left-0 right-[112px] z-20 overflow-visible'
export const DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_TRACK_CLASS = 'absolute inset-y-0 left-0 right-[80px] z-20 overflow-visible'
export const DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SIZE_CLASS = 'pointer-events-none absolute inset-0 z-30 h-full w-full'
export const DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SIZE_CLASS = 'pointer-events-none absolute left-0 top-1/2 z-20 h-[94px] w-full object-fill -translate-y-1/2'
export const DASHBOARD_RESKIN_POINTS_FILL_CLASS = 'pointer-events-none absolute inset-0 z-10 box-border h-full w-full'
export const DASHBOARD_RESKIN_POINTS_FILL_CAP_WIDTH_PX = 62
export const DASHBOARD_RESKIN_POINTS_NUMBER_INSET_PX = 40
export const DASHBOARD_RESKIN_POINTS_NUMBER_CLASS = 'absolute right-[40px] top-1/2 z-30 m-0 translate-x-1/2 -translate-y-1/2 p-0 font-bold tabular-nums leading-none tracking-tighter text-[22px] text-[#34E89E]'
export const DASHBOARD_RESKIN_FINISH_SHIELD_SIZE_CLASS = 'pointer-events-none absolute right-[32px] top-1/2 z-0 mt-[3px] h-[171px] w-[150px] -translate-y-1/2 overflow-visible'
export const DASHBOARD_RESKIN_FINISH_SHIELD_GLOW_CLASS = 'absolute -left-[50px] -top-[50px] h-[271px] w-[240px] max-w-none'
export const DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SIZE_CLASS = 'pointer-events-none absolute right-[32px] top-1/2 z-30 h-[173px] w-[153px] -translate-y-1/2 overflow-visible'
export const DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_IMAGE_CLASS = 'absolute -left-[50px] -top-[50px] h-[273px] w-[253px] max-w-none'
export const DASHBOARD_RESKIN_FOOTER_INSET_CLASS = 'pr-[40px]'

export const getDashboardReskinTileSizeClass = (
  _state: DashboardReskinTileState
): string => DASHBOARD_RESKIN_TILE_SIZE_CLASS

export const getDashboardReskinTileFlexClass = (
  _state: DashboardReskinTileState
): string => DASHBOARD_RESKIN_TILE_FLEX_CLASS

export const getDashboardReskinTileImageClass = (
  state: DashboardReskinTileState
): string => state === 'launch'
  ? DASHBOARD_RESKIN_LAUNCH_TILE_IMAGE_CLASS
  : DASHBOARD_RESKIN_TILE_IMAGE_CLASS

const DASHBOARD_RESKIN_DIR = '/assets/images/dashboard-reskin'

export const DASHBOARD_RESKIN_WORDMARK_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/ZenithShield Wordmark - SpeedGlow - 2tone.png`
)
export const DASHBOARD_RESKIN_MENU_ICON_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Menu Icon - PlayerPortal.png`
)
export const DASHBOARD_RESKIN_LIGHTNING_LAUNCH_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Lightning Round - LAUNCH.svg`
)
export const DASHBOARD_RESKIN_BACKGROUND_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Dashboard-Background-2026-9-18.png`
)
export const DASHBOARD_RESKIN_HEALTHBAR_OUTLINE_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/HealthBar Outline.svg`
)
export const DASHBOARD_RESKIN_HEALTHBAR_COMPLETED_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/HealthBar (30).svg`
)
export const DASHBOARD_RESKIN_FINISH_SHIELD_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Finish Shield.svg`
)
export const DASHBOARD_RESKIN_FINISH_SHIELD_COMPLETED_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Finish Shield (1).svg`
)
export const DASHBOARD_RESKIN_POINTS_FILL_SRC = encodeURI(
  `${DASHBOARD_RESKIN_DIR}/Points fill.svg`
)

export const getDashboardReskinPointsFillStyle = (): CSSProperties => ({
  borderStyle: 'solid',
  borderWidth: `0 ${DASHBOARD_RESKIN_POINTS_FILL_CAP_WIDTH_PX}px 0 0`,
  borderImageSource: `url(${DASHBOARD_RESKIN_POINTS_FILL_SRC})`,
  borderImageSlice: `0 ${DASHBOARD_RESKIN_POINTS_FILL_CAP_WIDTH_PX} 0 0 fill`,
  borderImageRepeat: 'stretch'
})

export const getDashboardReskinPointsFillWidthStyle = (fillWidth: number): CSSProperties => ({
  width: `calc(${DASHBOARD_RESKIN_POINTS_FILL_CAP_WIDTH_PX}px + (100% - ${DASHBOARD_RESKIN_POINTS_FILL_CAP_WIDTH_PX}px) * ${fillWidth} / 100)`
})

export const getDashboardContainerBackgroundStyle = (): CSSProperties => ({
  backgroundImage: `url(${DASHBOARD_RESKIN_BACKGROUND_SRC})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: '#000000'
})

interface DashboardReskinTileSet {
  launch?: string
  replay?: string
  locked?: Partial<Record<number, string>>
}

export const dashboardReskinTiles: Record<string, DashboardReskinTileSet> = {
  cocaine: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level1 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level1 - REPLAY.svg`
  },
  xanax: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level2 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level2 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level2 - LOCKED 1.svg`
    }
  },
  mdma: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level3 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level3 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level3 - LOCKED 1.svg`,
      2: `${DASHBOARD_RESKIN_DIR}/Level3 - LOCKED 2.svg`
    }
  },
  response: {
    launch: `${DASHBOARD_RESKIN_DIR}/Level4 - LAUNCH.svg`,
    replay: `${DASHBOARD_RESKIN_DIR}/Level4 - REPLAY.svg`,
    locked: {
      1: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 1.svg`,
      2: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 2.svg`,
      3: `${DASHBOARD_RESKIN_DIR}/Level4 - LOCKED 3.svg`
    }
  }
}

export const getDashboardReskinTileState = (
  module: { isCurrent?: boolean, isLocked?: boolean, name?: string },
  reviewState: boolean
): DashboardReskinTileState => {
  if (reviewState) {
    return 'replay'
  }
  if (module.isCurrent === true) {
    return 'launch'
  }
  if (module.isLocked === true) {
    // Level 1 is the starting tile. It is launch or replay, never locked.
    if (module.name?.toLowerCase() === 'cocaine') {
      return 'launch'
    }
    return 'locked'
  }
  return 'replay'
}

export const getDashboardReskinLockedIteration = (currentModuleIndex: number): number => {
  if (currentModuleIndex < 0) {
    return 1
  }
  return currentModuleIndex + 1
}

export const getDashboardReskinTileSrc = (
  moduleName: string,
  state: DashboardReskinTileState,
  lockedIteration: number = 1
): string | undefined => {
  const tiles = dashboardReskinTiles[moduleName.toLowerCase()]
  if (tiles == null) {
    return undefined
  }

  const src = state === 'locked'
    ? tiles.locked?.[lockedIteration]
    : tiles[state]
  if (src == null) {
    return undefined
  }
  return encodeURI(src)
}
